import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FileUploader } from "react-drag-drop-files";
import { PrimaryButton } from "../../elements/buttons";
import useUploadMedia from "../../../hooks/useUploadMedia";
import useCreateIngredient from "../../../hooks/catalog/ingredients/useCreateIngredient";
import useUpdateIngredient from "../../../hooks/catalog/ingredients/useUpdateIngredient";
import useGetIngredient from "../../../hooks/catalog/ingredients/useGetIngredient";
import Spinner from "../../spinner";
import { toast } from "react-hot-toast";
import SwitchToggle from "../../elements/switchtoggle/SwitchToggle";

const fileTypes = ["JPG", "PNG", "JPEG", "WEBP"];

const AddEditIngredientForm = ({ isEdit = false }) => {
  const { ingredientId } = useParams();
  const navigate = useNavigate();

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      media_id: "",
      in_filter: false,
      image_alt: "",
      meta_title: "",
      meta_description: "",
    },
  });

  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageId, setImageId] = useState("");
  const [imageAlt, setImageAlt] = useState("");

  // For editing, fetch ingredient details
  const { data: ingredientData } = useGetIngredient({
    ingredientId: isEdit ? ingredientId : null,
    onSuccess: (data) => {
      if (isEdit) {
        const resData = data?.data || {};

        setValue("name", resData.name || "");
        setValue("in_filter", resData.in_filter || false);
        setValue("meta_title", resData.meta_title || "");
        setValue("meta_description", resData.meta_description || "");

        if (resData.media_id) {
          setImagePreview(resData.media_url || "");
          setImageId(resData.media_id || "");
          setImageAlt(resData.image_alt || "");
        }

        if (resData.image_alt) {
          setImageAlt(resData.image_alt);
          setValue("image_alt", resData.image_alt);
        }
      }
    },
  });

  // useEffect(() => {
  //   if (isEdit && ingredientData?.data?.data) {
  //     const data = ingredientData.data.data;
  //     setValue("name", data.name || "");
  //     setValue("meta_title", data.meta_title || "");
  //     setValue("meta_description", data.meta_description || "");

  //     // Set image data
  //     if (data.media_id) {
  //       setImageId(data.media_id);
  //       setImagePreview(data.media_url || "");
  //       setValue("media_id", data.media_id);
  //     }

  //     if (data.image_alt) {
  //       setImageAlt(data.image_alt);
  //       setValue("image_alt", data.image_alt);
  //     }
  //   }
  // }, [ingredientData, isEdit, setValue]);

  // Image upload handlers
  const handleMediaUploadSuccess = (data) => {
    setImageId(data.data.image_id);
    setImagePreview(data.data.image_url);
    setValue("media_id", data.data.image_id);
    setImageUploading(false);
    toast.success("Image uploaded successfully", {
      position: "top-right",
      duration: 2000,
    });
  };

  const handleMediaUploadError = (error) => {
    setImageUploading(false);
    toast.error(error?.response?.data?.message || "Error uploading image", {
      position: "top-right",
      duration: 3000,
    });
  };

  const { upload } = useUploadMedia({
    url: "upload/image",
    onSuccess: handleMediaUploadSuccess,
    onError: handleMediaUploadError,
  });

  const handleImageUpload = (file) => {
    setImageUploading(true);
    upload.mutate({ thumbnail: file, screen: "INGREDIENT" });
  };

  const handleReUpload = () => {
    setImagePreview(null);
    setImageId("");
    setValue("media_id", "");
  };

  const { handleSubmit: handleCreate } = useCreateIngredient({
    reset: () => reset(),
  });

  const { handleSubmit: handleUpdate } = useUpdateIngredient();

  const onSubmit = (formData) => {
    const payload = {
      ...(isEdit && { ingredient_id: ingredientId }),
      name: formData.name,
      media_id: imageId,
      image_alt: imageAlt,
      meta_title: formData.meta_title || "",
      meta_description: formData.meta_description || "",
      in_filter: formData.in_filter || false,
    };

    if (isEdit) {
      handleUpdate(payload);
    } else {
      handleCreate(payload);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="py-4">
      <div className="mb-6">
        <Controller
          name="name"
          control={control}
          rules={{
            required: "Ingredient Name is required",
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Ingredient Name" className="label">
                Ingredient Name <small className="text-red-700">*</small>
              </label>
              <input
                required
                type="text"
                placeholder="Enter Ingredient Name"
                name="name"
                className="input"
                value={value}
                onChange={onChange}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <div className="mb-6">
        <label htmlFor="Ingredient Image" className="label">
          Image
        </label>
        <div className="w-52 h-52 mt-2">
          {imageUploading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Spinner />
              <p className="mt-2">Uploading image...</p>
            </div>
          ) : !imagePreview ? (
            <FileUploader
              name="ingredient_image"
              types={fileTypes}
              handleChange={handleImageUpload}
              classes="file-uploader"
              hoverTitle="Drop here"
            />
          ) : (
            <div className="border border-dashed border-blue-700 rounded flex flex-col items-center justify-center h-full p-2">
              <div className="relative w-full h-36 flex items-center justify-center">
                <img
                  src={imagePreview}
                  alt="ingredient"
                  className="max-h-36 max-w-full object-contain rounded shadow"
                />
              </div>
              <div className="w-full mt-2 flex flex-col items-center">
                <input
                  type="text"
                  className="input w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring focus:border-blue-400"
                  style={{ marginTop: 0 }}
                  placeholder="Image Alt Text (optional)"
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                />
                <button
                  type="button"
                  className="mt-2 text-blue-600 underline text-xs"
                  onClick={handleReUpload}
                >
                  Re-Upload
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="-ml-4 mt-6 mb-6">
          <Controller
            name="in_filter"
            control={control}
            render={({ field: { value, onChange } }) => (
              <div>
                <SwitchToggle
                  checkedValue={value}
                  label={"In filter?"}
                  onChange={(e) => onChange(e)}
                  name="in_filter"
                />
              </div>
            )}
          />
        </div>
      </div>

      <div className="border-b-2 border-black pt-2 pb-2 mb-6">
        <span className="text-2xl font-normal">Search Engine Optimization</span>
      </div>

      <div className="space-y-6 mb-6">
        <Controller
          name="meta_title"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Meta Title" className="label">
                Meta Title
              </label>
              <input
                type="text"
                placeholder="Enter Meta Title"
                name="meta_title"
                className="input"
                value={value}
                onChange={onChange}
              />
            </div>
          )}
        />

        <Controller
          name="meta_description"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Meta Description" className="label">
                Meta Description
              </label>
              <input
                type="text"
                placeholder="Enter Meta Description"
                name="meta_description"
                className="input"
                value={value}
                onChange={onChange}
              />
            </div>
          )}
        />
      </div>

      <div className="flex justify-end mt-8">
        <PrimaryButton label={isEdit ? "Update" : "Save"} type={"submit"} />
      </div>
    </form>
  );
};

export default AddEditIngredientForm;
